import { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { Server as ServerIco, Cpu, Gamepad2, Play, RefreshCw, Loader2, AlertTriangle, SlidersHorizontal } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Server } from '../../types';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { Input } from '../design/Input';
import { SettingsSection, SectionNav, Field, ToggleRow, type SectionNavItem } from './settings/primitives';
import { RamField } from './settings/RamField';
import { GameRulesFields, validateProps } from './settings/GameRulesFields';
import { SteamGameFields, validateSteamConfig } from './settings/SteamGameFields';
import { ArkGameplayFields } from './settings/ArkGameplayFields';
import { LaunchFields, type LaunchMode } from './settings/LaunchFields';
import { AutoUpdateFields, DEFAULT_AUTO_UPDATE, type AutoUpdate } from './settings/AutoUpdateFields';
import { DangerZone } from './settings/DangerZone';
import { SaveBar } from './settings/SaveBar';
import { can } from '@/utils/permissions';

/** Die Panel-Felder des Servers (DB) in Formularform. */
interface Panel {
  name: string;
  port: number | '';
  custom_domain: string;
  ram_limit: number;
  ignore_ram_limit: boolean;
  java_path: string;
  launch_mode: LaunchMode;
  jar_file: string;
  auto_update: AutoUpdate;
}

const panelOf = (s: Server): Panel => ({
  name: s.name,
  port: s.port,
  custom_domain: s.custom_domain || '',
  ram_limit: s.ram_limit,
  ignore_ram_limit: !!s.ignore_ram_limit,
  java_path: s.java_path || 'java',
  launch_mode: s.launch_config?.mode || 'default',
  jar_file: s.launch_config?.jar_file || '',
  auto_update: { ...DEFAULT_AUTO_UPDATE, ...(s.auto_update_config || {}) } as AutoUpdate,
});

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const changedKeys = (draft: Record<string, any> | null | undefined, base: Record<string, any> | null | undefined) =>
  draft && base ? Object.keys(draft).filter((k) => !same(draft[k], base[k])) : [];

/** Panel-Felder, die ein laufender Server erst nach einem Neustart übernimmt. */
const RESTART_PANEL = ['port', 'ram_limit', 'java_path', 'launch_mode', 'jar_file'];
/** server.properties-Felder, die per Konsolenbefehl sofort wirken (Java). */
const LIVE_PROPS = ['difficulty', 'white-list'];

/** Nur geänderte Felder schicken – so überschreibt man nichts, was jemand anderes parallel geändert hat. */
function panelPayload(keys: string[], p: Panel) {
  const out: Record<string, any> = {};
  for (const k of keys) {
    if (k === 'launch_mode' || k === 'jar_file') out.launch_config = { mode: p.launch_mode, jar_file: p.launch_mode === 'custom_jar' ? p.jar_file : undefined };
    else if (k === 'auto_update') out.auto_update_config = p.auto_update;
    else if (k === 'port') out.port = Number(p.port);
    else out[k] = (p as any)[k];
  }
  return out;
}

const Loading = () => (
  <div className="flex items-center gap-2 text-sm text-fg-muted py-2">
    <Loader2 size={15} className="animate-spin" /> Lade Einstellungen …
  </div>
);

/**
 * Einstellungen eines Servers (alle Spiele): immer bearbeitbar, in Abschnitten,
 * mit einer Speichern-Leiste, die unten mitläuft, sobald etwas geändert ist.
 * Spielabhängig: Minecraft → server.properties + Start & Java, Steam → Spiel-Config.
 */
export const ServerSettingsCard = observer(({ server }: { server: Server }) => {
  const cu = userStore.currentUser;
  const canEdit = can(cu, 'can_configure_server');
  const canPower = can(cu, 'can_power_server');
  const isSteam = String(server.type || '').startsWith('steam');
  const kind: 'java' | 'bedrock' | 'steam' = isSteam ? 'steam' : server.type === 'bedrock' ? 'bedrock' : 'java';
  const online = server.status === 'online';
  const location = useLocation();

  // --- Panel-Felder (DB) ---
  const base = panelOf(server);
  const baseKey = JSON.stringify(base);
  const baseRef = useRef<Panel>(base);
  const [panel, setPanel] = useState<Panel>(base);
  // Neue Serverdaten (Socket, andere Admins) übernehmen – aber nur in Feldern, die hier niemand angefasst hat.
  useEffect(() => {
    const next = JSON.parse(baseKey) as Panel;
    const prevBase = baseRef.current;
    setPanel((prev) => {
      const merged: any = { ...prev };
      for (const k of Object.keys(next) as (keyof Panel)[]) if (same(prev[k], prevBase[k])) merged[k] = next[k];
      return merged;
    });
    baseRef.current = next;
  }, [baseKey]);
  const set = <K extends keyof Panel>(k: K, v: Panel[K]) => setPanel((p) => ({ ...p, [k]: v }));

  // --- Spiel-Einstellungen (server.properties bzw. gamemanager-steam.json) ---
  const [props, setProps] = useState<{ base: Record<string, any>; draft: Record<string, any> } | null>(null);
  const [steam, setSteam] = useState<{ game: any; slug?: string; base: Record<string, any>; draft: Record<string, any> } | null>(null);
  const [gameError, setGameError] = useState(false);
  useEffect(() => {
    let alive = true;
    if (kind === 'steam') {
      serverStore.fetchSteamConfig(server.id).then((d) => {
        if (!alive) return;
        if (d?.meta) {
          // ARK-Spielregeln: Ausgangsstand = was gerade gilt (sonst stünde sofort „ungespeichert“ da).
          const cfg = { ...(d.meta.config || {}), ...(d.gameplayCurrent ? { gameplay: d.gameplayCurrent } : {}) };
          setSteam({ game: d.game, slug: d.meta.slug, base: cfg, draft: cfg });
        }
        else setGameError(true);
      });
    } else {
      serverStore.fetchProperties(server.id).then((d) => {
        if (!alive) return;
        if (d) setProps({ base: d.values, draft: d.values });
        else setGameError(true);
      });
    }
    return () => { alive = false; };
  }, [server.id, kind]);

  const panelKeys = changedKeys(panel as any, base as any);
  const propKeys = changedKeys(props?.draft, props?.base);
  const steamKeys = changedKeys(steam?.draft, steam?.base);
  const dirtyCount = panelKeys.length + propKeys.length + steamKeys.length;
  const needsRestart = online && (
    panelKeys.some((k) => RESTART_PANEL.includes(k)) ||
    propKeys.some((k) => kind !== 'java' || !LIVE_PROPS.includes(k)) ||
    steamKeys.length > 0
  );

  const [saving, setSaving] = useState(false);
  const [restartPending, setRestartPending] = useState(false);
  useEffect(() => { if (!online) setRestartPending(false); }, [online]);

  const validate = (): string | null => {
    if (!panel.name.trim()) return 'Bitte gib dem Server einen Namen.';
    const port = Number(panel.port);
    if (!Number.isInteger(port) || port < 1 || port > 65535) return 'Der Port muss eine Zahl zwischen 1 und 65535 sein.';
    if (panel.launch_mode === 'custom_jar' && !panel.jar_file) return 'Bitte wähle eine Startdatei – oder stell die Startart auf „Standard".';
    if (props && kind !== 'steam') { const e = validateProps(kind, props.draft); if (e) return e; }
    if (steam) { const e = validateSteamConfig(steam.slug, steam.draft); if (e) return e; }
    return null;
  };

  const save = async (restartAfter = false) => {
    if (saving || dirtyCount === 0) return;
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    let ok = true;
    let restart = false;
    try {
      if (panelKeys.length) {
        ok = await serverStore.handleSaveServer({ id: server.id, ...panelPayload(panelKeys, panel) }, { silent: true });
        if (ok && online && panelKeys.some((k) => RESTART_PANEL.includes(k))) restart = true;
      }
      if (ok && props && propKeys.length) {
        const r = await serverStore.saveProperties(server.id, Object.fromEntries(propKeys.map((k) => [k, props.draft[k]])));
        if (r) {
          setProps({ base: r.values, draft: r.values });
          if (r.restartNeeded.length) restart = true;
        } else ok = false;
      }
      if (ok && steam && steamKeys.length) {
        const patch = Object.fromEntries(steamKeys.map((k) => [k, steam.draft[k]]));
        if (await serverStore.saveSteamConfig(server.id, patch, { silent: true })) {
          setSteam({ ...steam, base: steam.draft });
          if (online) restart = true;
        } else ok = false;
      }
    } finally {
      setSaving(false);
    }
    if (!ok) return;
    if (restartAfter && online) {
      setRestartPending(false);
      toast.success('Gespeichert – der Server startet neu.');
      serverStore.powerServer(server.id, 'restarting');
      return;
    }
    setRestartPending(restart);
    toast.success(restart ? 'Gespeichert – einiges wirkt erst nach einem Neustart.' : 'Einstellungen gespeichert');
  };

  const discard = () => {
    setPanel(base);
    if (props) setProps({ ...props, draft: props.base });
    if (steam) setSteam({ ...steam, draft: steam.base });
  };

  // Strg+S speichert; Tab schließen mit offenen Änderungen fragt nach.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        saveRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (!dirtyCount) return;
    const onUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirtyCount]);

  const jump = (id: string) => document.getElementById(`set-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // „Bearbeiten" in der Übersicht springt direkt zum Spiel-Abschnitt.
  useEffect(() => {
    const target = (location.state as any)?.section;
    if (target) setTimeout(() => jump(target), 120);
  }, [location.key]);

  const portClash = serverStore.servers.find(
    (s) => s.id !== server.id && s.status !== 'archived' && Number(s.port) === Number(panel.port),
  );
  const portHint = portClash
    ? <span className="text-warn">Auch „{portClash.name}" nutzt diesen Port – beide können nicht gleichzeitig laufen.</span>
    : kind === 'java' ? 'Standard ist 25565. Spieler geben die Adresse mit Port ein (z.B. …:25565). Wird automatisch in server.properties übernommen.'
      : kind === 'bedrock' ? 'Standard ist 19132.'
        : 'Spiel-Port; die übrigen Ports (Serverbrowser, RCON) werden daraus abgeleitet.';

  const isAdmin = can(cu, 'can_delete_servers');
  // Jeder Bereich hat seine Farbe – im Chip oben, im Symbol und im Streifen der Karte.
  const nav: SectionNavItem[] = [
    { id: 'allgemein', label: 'Allgemein', icon: <ServerIco size={12} />, tone: 'sky' },
    { id: 'leistung', label: 'Leistung', icon: <Cpu size={12} />, tone: 'violet' },
    { id: 'spiel', label: kind === 'steam' ? 'Spiel' : 'Spielwelt', icon: <Gamepad2 size={12} />, tone: 'green' },
    ...(steam?.game?.gameplay ? [{ id: 'regeln', label: 'Spielregeln', icon: <SlidersHorizontal size={12} />, tone: 'ember' }] satisfies SectionNavItem[] : []),
    ...(kind === 'java' ? [{ id: 'start', label: 'Start & Java', icon: <Play size={12} />, tone: 'amber' }] satisfies SectionNavItem[] : []),
    { id: 'updates', label: 'Updates', icon: <RefreshCw size={12} />, tone: 'teal' },
    ...(isAdmin ? [{ id: 'gefahr', label: 'Gefahrenzone', icon: <AlertTriangle size={12} />, tone: 'red' }] satisfies SectionNavItem[] : []),
  ];

  return (
    <div className="max-w-5xl animate-in fade-in duration-300">
      <div className="flex flex-wrap items-end justify-between gap-4 pb-1">
        <div>
          <h2 className="font-display text-xl font-semibold text-fg tracking-tight">Einstellungen</h2>
          <p className="text-sm text-fg-muted mt-1">
            {canEdit
              ? 'Einfach ändern – unten erscheint dann eine Leiste zum Speichern.'
              : 'Nur Admins können Einstellungen ändern.'}
          </p>
        </div>
      </div>
      <SectionNav items={nav} prefix="set-" />

      <fieldset disabled={!canEdit} className="min-w-0 m-0 p-0 border-0">
        <SettingsSection id="set-allgemein" tone="sky" icon={<ServerIco size={16} />} title="Allgemein" description="Name im Panel, Port und die Adresse, die Spieler eintippen.">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Name">
              <Input value={panel.name} onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="Port" hint={portHint}>
              <Input
                type="number"
                inputMode="numeric"
                value={panel.port}
                onChange={(e) => set('port', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
              />
            </Field>
          </div>
          <Field label="Eigene Domain (optional)" hint="Wird als Beitritts-Adresse angezeigt. Braucht einen DNS-Eintrag (A-Record) auf diese IP.">
            <Input value={panel.custom_domain} placeholder="z.B. mc.meinserver.de" onChange={(e) => set('custom_domain', e.target.value.trim())} />
          </Field>
        </SettingsSection>

        <SettingsSection id="set-leistung" tone="violet" icon={<Cpu size={16} />} title="Leistung" description="Wie viel Arbeitsspeicher der Server bekommt – die Leiste zeigt, was die anderen laufenden Server schon belegen.">
          <RamField serverId={server.id} value={panel.ram_limit} saved={server.ram_limit} running={online} onChange={(v) => set('ram_limit', v)} />
          <div className="pt-5 border-t border-line">
            <ToggleRow
              label="Bei RAM-Überlast nicht neu starten"
              hint="Normalerweise startet das Panel einen Server neu, der deutlich mehr RAM braucht als eingestellt. Nur einschalten, wenn er dadurch ständig neu startet."
              value={panel.ignore_ram_limit}
              onChange={(v) => set('ignore_ram_limit', v)}
            />
          </div>
        </SettingsSection>

        <SettingsSection
          id="set-spiel"
          tone="green"
          icon={<Gamepad2 size={16} />}
          title={kind === 'steam' ? 'Spiel' : 'Spielwelt'}
          description={kind === 'steam'
            ? 'Name im Serverbrowser, Passwort und Karte. Wirkt ab dem nächsten Start.'
            : 'Die wichtigsten Regeln aus server.properties – ohne die Datei von Hand zu bearbeiten.'}
        >
          {gameError ? (
            <p className="text-sm text-warn">Die Spiel-Einstellungen konnten nicht geladen werden. Seite neu laden oder im Tab Dateien nachsehen.</p>
          ) : kind === 'steam' ? (
            steam
              ? <SteamGameFields game={steam.game} cfg={steam.draft} onChange={(k, v) => setSteam((s) => s && { ...s, draft: { ...s.draft, [k]: v } })} />
              : <Loading />
          ) : props ? (
            <GameRulesFields kind={kind} values={props.draft} online={online} onChange={(k, v) => setProps((p) => p && { ...p, draft: { ...p.draft, [k]: v } })} />
          ) : (
            <Loading />
          )}
        </SettingsSection>

        {kind === 'steam' && steam?.game?.gameplay && steam.draft.gameplay && (
          <SettingsSection
            id="set-regeln"
            tone="ember"
            icon={<SlidersHorizontal size={16} />}
            title="Spielregeln"
            description="XP, Zähmen, Ernte, Ausdauer, Hunger, Zucht … Das Panel schreibt die Werte bei jedem Start in die ARK-Konfiguration – Änderungen wirken ab dem nächsten Neustart."
          >
            <ArkGameplayFields
              fields={steam.game.gameplay}
              values={steam.draft.gameplay}
              onChange={(g) => setSteam((s) => s && { ...s, draft: { ...s.draft, gameplay: g } })}
            />
          </SettingsSection>
        )}

        {kind === 'java' && (
          <SettingsSection id="set-start" tone="amber" icon={<Play size={16} />} title="Start & Java" description="Wie der Server gestartet wird. Die Standardwerte passen fast immer.">
            <LaunchFields
              serverId={server.id}
              launchMode={panel.launch_mode}
              jarFile={panel.jar_file}
              javaPath={panel.java_path}
              onChange={(patch) => setPanel((p) => ({ ...p, ...patch }))}
            />
          </SettingsSection>
        )}

        <SettingsSection id="set-updates" tone="teal" icon={<RefreshCw size={16} />} title="Automatische Updates" description="Neue Versionen selbst installieren – mit Backup vorher und Rückweg, falls etwas schiefgeht.">
          <AutoUpdateFields value={panel.auto_update} onChange={(v) => set('auto_update', v)} serverType={server.type} />
        </SettingsSection>
      </fieldset>

      <DangerZone server={server} />

      {canEdit && (
        <SaveBar
          dirtyCount={dirtyCount}
          saving={saving}
          needsRestart={needsRestart}
          canRestart={canPower && online}
          restartPending={restartPending && online}
          onSave={() => save(false)}
          onSaveRestart={() => save(true)}
          onDiscard={discard}
          onRestartNow={() => { setRestartPending(false); serverStore.powerServer(server.id, 'restarting'); }}
          onDismissRestart={() => setRestartPending(false)}
        />
      )}
    </div>
  );
});
